/*
 * File: BulkSiteInput.tsx
 * Project: papa-bear
 * -----
 * Last Modified: 17 May 2021 2:13:24 pm
 * -----
 * Copyright (c) 2021 Elegant Software Solutions All rights reserved.
 */

import { Select, Space, Spin } from 'antd';
import React, { FC, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setBulkSiteValue, setBulkWarehouseValue } from 'redux/productsInfo/bulkApplySlice';
import { ReduxState } from 'redux/rootReducer';
import { useGetSitesQuery } from 'redux/services/useKingstonLivingstonService';
import { useDebouncedCallback } from 'use-debounce';
import { BlockLoader } from '../BlockLoader';

export const BulkSiteInput: FC = () => {
  const { selectedCompanyDataAreaId: dataAreaId } = useSelector((state: ReduxState) => state.salesOrders);
  const dispatch = useDispatch();
  const { bulkSiteValue, bulkWarehouseValue } = useSelector((state: ReduxState) => state.bulkApply);

  const [searchValue, _setSearchValue] = useState<string | undefined>(undefined);
  const [offset, _setOffset] = useState(0);

  /* ****************** Hook API ****************** */
  const { data: sitesResponse, isFetching, isLoading } = useGetSitesQuery({ siteContains: searchValue, offset, dataAreaId });

  /* ****************** JSX / Variables ****************** */
  const sitesSearchOptions = useMemo(() => sitesResponse?.data?.map((site) => ({ label: site.siteId, value: site.siteId })), [sitesResponse]);

  /* ****************** Functions ****************** */
  // Handle delay of searching data
  const handleSearch = useDebouncedCallback((value: string | undefined) => {
    _setOffset(0);
    _setSearchValue(value);
  }, 600);

  const handleClear = (): void => {
    dispatch(setBulkSiteValue(undefined));
    _setOffset(0);
  };

  const handleScroll = (event: React.UIEvent<HTMLDivElement>): void => {
    const target = event.currentTarget;

    if (!isLoading && !isFetching && target.scrollTop + target.offsetHeight === target.scrollHeight && sitesResponse && sitesResponse?.totalCount - offset > 25) {
      _setOffset(offset + 25);
    }
  };

  const handleSelect = (value: string): void => {
    dispatch(setBulkSiteValue(value));
    dispatch(setBulkWarehouseValue(undefined));
  };

  const dropDownRender = (menu: JSX.Element): JSX.Element => {
    if ((isLoading || isFetching) && !offset)
      return (
        <div style={{ textAlign: 'center', padding: 10 }}>
          <Spin indicator={<BlockLoader direction="loader loader--slideUp" />} spinning />
        </div>
      );

    const moreResultsJSX =
      (isLoading || isFetching) && offset ? (
        <div style={{ padding: '0px 10px' }}>
          <Space>
            <Spin size="small" indicator={<BlockLoader direction="loader loader--slideUp" />} />
          </Space>
        </div>
      ) : null;

    return (
      <div>
        {menu}
        {moreResultsJSX}
      </div>
    );
  };

  /* ****************** Render ****************** */
  return (
    <Select
      allowClear
      dropdownRender={dropDownRender}
      placeholder="Select a Site Id"
      dropdownMatchSelectWidth={false}
      filterOption={false}
      loading={isLoading || isFetching}
      onClear={handleClear}
      onPopupScroll={handleScroll}
      onSearch={handleSearch}
      onSelect={handleSelect}
      options={sitesSearchOptions}
      showSearch
      value={bulkSiteValue}
    />
  );
};
